import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { THEME_COLORS, type ThemeColor } from '../../types/theme';
import Switch from '../primitives/Switch';

interface ColorPickerProps {
  currentColor: ThemeColor;
  useSystemColor: boolean;
  onColorChange: (color: ThemeColor) => void;
  onSystemColorChange: (checked: boolean) => void;
  className?: string;
  /** ARIA role for each color button (e.g. "menuitem" when used inside a menu) */
  itemRole?: string;
}

function ColorPicker({
  currentColor,
  useSystemColor,
  onColorChange,
  onSystemColorChange,
  className,
  itemRole,
}: ColorPickerProps) {
  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      <div className="flex justify-between gap-1">
        {THEME_COLORS.map((themeColor) => (
          <button
            key={themeColor}
            onClick={() => onColorChange(themeColor)}
            className={clsx(
              'duration-normal h-6 flex-1 rounded border transition-colors',
              currentColor === themeColor && !useSystemColor ? 'border-2' : 'border',
            )}
            style={{
              backgroundColor:
                themeColor === 'light'
                  ? 'var(--white-100)'
                  : themeColor === 'sepia'
                    ? 'var(--sepia-100)'
                    : themeColor === 'storm'
                      ? 'var(--neutral-800)'
                      : 'var(--neutral-900)',
              borderColor:
                currentColor === themeColor && !useSystemColor
                  ? '#0074bf'
                  : 'var(--border-strong)',
            }}
            role={itemRole}
            aria-label={t`${themeColor} theme`}
          />
        ))}
      </div>
      <div className="flex w-full items-center justify-between gap-1">
        <span className="text-sm">
          <Trans>Auto Dark Mode</Trans>
        </span>
        <Switch
          checked={useSystemColor}
          onChange={onSystemColorChange}
          aria-label={t`Toggle system default theme`}
        />
      </div>
    </div>
  );
}

export default ColorPicker;
